import { Film } from '../services';
import { IntlShape } from 'react-intl';

/* A CMS title that already names the brand was written by hand for search, e.g.
   "SHABAB ALBOMB | Now Showing | Book Tickets | muvi Cinemas", and is used as is. */
const BRAND_PATTERN = /muvi|موڤي|موفي/i;

/**
 * The movie page's <title>.
 *
 * Almost every film in the CMS has its `titleTag` set to the bare movie name
 * (78 of 79 on 2026-09-21), so the page went out titled "AVENGERS: ENDGAME
 * ENCORE": no brand, and no hint that tickets can be booked here. Those are the
 * words a search for a film at a cinema matches on, and competitors carry them.
 * The rest is completed here, so the CMS stays free to override it.
 *
 * The title carries no status word. It used to read "<name> | Coming Soon | …"
 * for unreleased films, but nobody searches "coming soon": it spent the scarce
 * ~60 characters on a phrase that matches no query, and pushed the brand past
 * the cut. The brand instead names the country, so a search like "digger in
 * riyadh" has some geography to match — our pages carried none, while VOX's
 * did, and VOX took the AI Overview and position 1 for that query.
 *
 * "Book Tickets" is unconditional. Every film page is where a booking starts,
 * including one whose seats are not on sale yet, and the phrase carries the
 * booking intent competitors rank on.
 * @param {Film} film - the film being rendered
 * @param {IntlShape} intl - for the localized parts of the suffix
 * @returns The title, e.g. "THE ODYSSEY | Book Tickets | muvi Cinemas Saudi Arabia".
 */
export const buildMovieSeoTitle = (film: Film, intl: IntlShape): string => {
  const name = (film?.titleTag || film?.title || '').trim();
  if (!name || BRAND_PATTERN.test(name)) return name;

  return [
    name,
    intl.formatMessage({ id: 'seo.movie.book_tickets' }),
    intl.formatMessage({ id: 'seo.site_name_country' }),
  ].join(' | ');
};
